import type {
	BeckyFunctionLibrary,
	BeckyModel,
	BeckyRequestPayload,
	OoiCssInstanceModel,
} from '@wix/thunderbolt-becky-root'
import type { AsyncNodes, AsyncStores } from '@wix/thunderbolt-async-state-manager-store'
import { getAsyncStore } from '@wix/thunderbolt-async-state-manager-store'
import { resolveBeckyModel } from '../helpers/resolveBeckyModel'
import type { GetStylableInstanceParams } from '../stylable'
import { getStylableInstance } from '../stylable'
import type { BeckyExperiments } from '@wix/thunderbolt-commons'
import type { BeckyTopology, FetchFn, Logger, MetricsReporter, ModuleFetcher } from '@wix/thunderbolt-becky-types'
import { getOoiCssModel, loadCssConfigs } from '../helpers/ooiCssProcessor'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'
import { resolveWidgetsWithAppSettings } from '../styleParamsOverrides/resolveAppSettings'
import { addInvalidation as addShapesInvalidation, fetchShapes } from '@wix/thunderbolt-fetch-shapes'
import LRU from 'lru-cache'
import { getCompIdToPathToSelector } from './compIdToPathToSelector'
import { getBuilderTypeToReducedManifest, getCompIdToReducedManifest } from '../utils/builderManifestFetch'

const stylableCache = new LRU<string, any>({ max: 20 })

export const getStylable = async (
	payload: BeckyRequestPayload,
	beckyTopology: BeckyTopology,
	experiments: BeckyExperiments,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	funcLib: BeckyFunctionLibrary
) => {
	const shouldSkipInitStylableInstance =
		(payload.featuresToRun?.length && !payload.featuresToRun?.includes('stylableCss')) ||
		payload.featuresToIgnore?.includes('stylableCss')

	let stylableInstance
	let partialStylableInstanceParams: Partial<GetStylableInstanceParams> = {}
	if (!shouldSkipInitStylableInstance) {
		const themeData = await metricsReporter.runAsyncAndReport(async () => {
			return (await payload.pages.masterPage).data.theme_data.THEME_DATA
		}, 'tb-css-get-theme-data')

		const themeFontsSpxResolved = funcLib.resolveThemeFontsSpx(themeData.font)
		partialStylableInstanceParams = {
			payload,
			fileRepoUrl: beckyTopology.environmentTopology.fileRepoUrl,
			themeData,
			themeFontsSpxResolved,
			staticMediaUrl: beckyTopology.publicTopology.staticMediaUrl,
		}
		stylableInstance = await metricsReporter.runAsyncAndReport(async () => {
			return getStylableInstance({
				...partialStylableInstanceParams,
				fetch: fetchFn,
				metricsReporter,
				cache: stylableCache,
			} as GetStylableInstanceParams)
		}, 'tb-css-get-stylable-instance')
	}
	return {
		stylableInstance,
		partialStylableInstanceParams,
	}
}

export const getBeckyModel = async (
	payload: BeckyRequestPayload,
	beckyTopology: BeckyTopology,
	funcLib: BeckyFunctionLibrary,
	moduleFetcher: ModuleFetcher,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	logger: Logger,
	experiments: BeckyExperiments
) => {
	const { stylableInstance, partialStylableInstanceParams } = await getStylable(
		payload,
		beckyTopology,
		experiments,
		fetchFn,
		metricsReporter,
		funcLib
	)
	payload.stylableInstance = stylableInstance
	const beckyModel = await funcLib.runAsyncAndReport(
		() => resolveBeckyModel({ payload, beckyTopology, fetchFn, metricsReporter, logger }),
		'resolveBeckyModel'
	)

	const asyncNodes: AsyncNodes = [
		{
			addInvalidation: addShapesInvalidation,
			afterInvalidation: (shapes) => {
				return funcLib.runAsyncAndReport(() => {
					return fetchShapes(
						shapes,
						fetchFn,
						beckyTopology.environmentTopology.mediaRootUrl,
						funcLib.buildSvgUrl,
						metricsReporter
					)
				}, 'tb-css-fetch-shapes')
			},
			name: 'shapeStore',
		},
	]

	const compIdToReducedManifest = await getCompIdToReducedManifest(beckyModel.structure, beckyModel.clientSpecMap, fetchFn, logger, beckyModel.isBuilderComponentModel)

	beckyModel.compIdToPathToSelector = await getCompIdToPathToSelector(
		compIdToReducedManifest
	)

	const builderTypeToReducedManifest = await getBuilderTypeToReducedManifest(
		beckyModel.structure,
		compIdToReducedManifest
	)

	beckyModel.builderTypeToReducedManifest = builderTypeToReducedManifest

	const asyncStorePromise = funcLib.runAsyncAndReport(
		async () => getAsyncStore(asyncNodes, beckyModel, metricsReporter, schemas),
		'tb-css-async-stores'
	) as Promise<AsyncStores>

	const ooiCssModel: OoiCssInstanceModel = funcLib.runAndReport(
		() => getOoiCssModel(beckyModel),
		'ooiCssModelFactory'
	)

	const asyncStore = await asyncStorePromise

	const ooiCssConfigStore = await loadCssConfigs(
		payload.ooiVersions!,
		ooiCssModel.widgetsIds,
		moduleFetcher,
		funcLib.runAsyncAndReport,
		logger
	)

	const ooiWidgetsAppSettings = await resolveWidgetsWithAppSettings(
		ooiCssModel.widgetsWithReferenceId,
		beckyModel.clientSpecMap,
		metricsReporter
	)

	const beckyModelWithConfigsAndAsyncStores: BeckyModel = {
		...beckyModel,
		...asyncStore,
		ooiCssConfigStore,
		ooiWidgetsAppSettings,
		featuresToRun: payload.featuresToRun ? payload.featuresToRun.split(',') : [],
		featuresToIgnore: payload.featuresToIgnore ? payload.featuresToIgnore.split(',') : [],
	}
	return { beckyModel: beckyModelWithConfigsAndAsyncStores, partialStylableInstanceParams }
}
